import { useState, useEffect, useRef, useCallback } from 'react';
import { subjectAPI, chapterAPI, contentAPI, mcqAPI } from '../../utils/api';
import PowerPointViewer from '../PowerPointViewer/PowerPointViewer';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { toast } from 'react-toastify';
import {
  RiFileLine, RiLinksLine, RiVideoLine, RiFileTextLine,
  RiQuestionLine, RiSlideshowLine, RiArrowLeftLine,
  RiFullscreenLine, RiBookOpenLine, RiTimeLine, RiPercentLine,
  RiEyeLine, RiExternalLinkLine,
} from 'react-icons/ri';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

/* ── PdfViewer (unchanged logic) ──────────────────────────────── */
const PdfViewer = ({ src, style }) => {
  const [numPages, setNumPages]     = useState(null);
  const [error, setError]           = useState(null);
  const [pdfData, setPdfData]       = useState(null);
  const containerRef                = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);

  useEffect(() => {
    setPdfData(null); setError(null); setNumPages(null);
    const token = localStorage.getItem('token');
    fetch(src, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('pdf')) throw new Error('File not found on server');
        return res.arrayBuffer();
      })
      .then(buffer => setPdfData(buffer))
      .catch(err => setError(err.message));
  }, [src]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => { setContainerWidth(entries[0].contentRect.width || 800); });
    ro.observe(containerRef.current);
    setContainerWidth(containerRef.current.offsetWidth || 800);
    return () => ro.disconnect();
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => setNumPages(numPages), []);

  if (error) return (
    <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#dc2626', fontSize: 14 }}>
      Failed to load PDF: {error}
    </div>
  );
  if (!pdfData) return (
    <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#64748b', fontSize: 14 }}>
      Loading PDF…
    </div>
  );
  return (
    <div ref={containerRef} style={{ ...style, overflowY: 'auto', background: '#525659' }}>
      <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess} onLoadError={err => setError(err.message)}
        loading={<div style={{ color: '#fff', padding: 20, textAlign: 'center' }}>Rendering pages…</div>}>
        {Array.from({ length: numPages || 0 }, (_, i) => (
          <div key={i + 1} style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}>
            <Page pageNumber={i + 1} width={containerWidth - 32} renderTextLayer={false} renderAnnotationLayer={false} />
          </div>
        ))}
      </Document>
    </div>
  );
};

const CONTENT_TYPES = [
  { id: 'content',   label: 'Content Files',  icon: RiFileLine,      color: 'blue' },
  { id: 'reference', label: 'References',      icon: RiLinksLine,      color: 'indigo' },
  { id: 'video',     label: 'Videos',          icon: RiVideoLine,     color: 'red' },
  { id: 'question',  label: 'Question Papers', icon: RiFileTextLine,  color: 'orange' },
  { id: 'mcq',       label: 'MCQ Tests',       icon: RiQuestionLine,  color: 'purple' },
  { id: 'slides',    label: 'Slides',          icon: RiSlideshowLine, color: 'green' },
];

/* ── TeacherMaterials ─────────────────────────────────────────── */
const TeacherMaterials = ({ classes: initialClasses }) => {
  const [classes]                             = useState(initialClasses || []);
  const [selectedClass, setSelectedClass]     = useState('');
  const [subjects, setSubjects]               = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [chapters, setChapters]               = useState([]);
  const [selectedChapter, setSelectedChapter] = useState('');
  const [contents, setContents]               = useState([]);
  const [mcqs, setMcqs]                       = useState([]);
  const [activeType, setActiveType]           = useState(null);
  const [selectedItem, setSelectedItem]       = useState(null);
  const [loading, setLoading]                 = useState(false);
  const [selectedMCQForView, setSelectedMCQForView]   = useState(null);
  const [showMCQQuestionsModal, setShowMCQQuestionsModal] = useState(false);
  const [fullscreenFile, setFullscreenFile]   = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [powerPointFile, setPowerPointFile]   = useState(null);

  useEffect(() => {
    if (!selectedClass) { setSubjects([]); setSelectedSubject(''); return; }
    setLoading(true);
    subjectAPI.getSubjectsByClass(selectedClass)
      .then(r => setSubjects(r.data.subjects || []))
      .catch(() => setSubjects([]))
      .finally(() => setLoading(false));
    setSelectedSubject(''); setChapters([]); setSelectedChapter(''); setContents([]); setActiveType(null); setSelectedItem(null);
  }, [selectedClass]);

  useEffect(() => {
    if (!selectedSubject) { setChapters([]); setSelectedChapter(''); return; }
    setLoading(true);
    chapterAPI.getChaptersBySubject(selectedSubject)
      .then(r => setChapters(r.data.chapters || []))
      .catch(() => setChapters([]))
      .finally(() => setLoading(false));
    setSelectedChapter(''); setContents([]); setActiveType(null); setSelectedItem(null);
  }, [selectedSubject]);

  useEffect(() => {
    if (!selectedChapter) { setContents([]); setMcqs([]); setActiveType(null); setSelectedItem(null); return; }
    setLoading(true);
    Promise.all([contentAPI.getChapterContents(selectedChapter), mcqAPI.getMCQByChapter(selectedChapter)])
      .then(([contentRes, mcqRes]) => {
        const items = contentRes.data.contents || [];
        setContents(items);
        setMcqs(mcqRes.data.mcqs || []);
        const firstType = CONTENT_TYPES.filter(t => t.id !== 'mcq').find(t => items.some(c => c.type === t.id));
        setActiveType(firstType?.id || null);
        setSelectedItem(null);
      })
      .catch(() => { setContents([]); setMcqs([]); })
      .finally(() => setLoading(false));
  }, [selectedChapter]);

  const fileUrl = (filePath, itemId) => {
    if (!filePath) return null;
    if (itemId) return `${API_BASE}/api/content/file/${itemId}`;
    return `${API_BASE}/api/content/file/${filePath.replace(/\\/g, '/').split('/').pop()}`;
  };

  const typeItems   = contents.filter(c => c.type === activeType);
  const chapterObj  = chapters.find(c => c._id === selectedChapter);
  const subjectObj  = subjects.find(s => s._id === selectedSubject);
  const classObj    = classes.find(c => c._id === selectedClass);

  const isYouTube    = (url) => url && (url.includes('youtube.com') || url.includes('youtu.be'));
  const youtubeEmbed = (url) => {
    if (!url) return null;
    const match = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const handleCopyLink = (shareCode) => {
    navigator.clipboard.writeText(`${window.location.origin}/mcq/${shareCode}`);
    toast.success('Share link copied to clipboard');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in-up">
      {/* Selector */}
      <div className="card p-5 mb-5">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Browse Materials</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label">Class</label>
            <select className="input" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
              <option value="">Select class</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Subject</label>
            <select className="input" value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)} disabled={!selectedClass || subjects.length === 0}>
              <option value="">Select subject</option>
              {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Chapter</label>
            <select className="input" value={selectedChapter} onChange={e => setSelectedChapter(e.target.value)} disabled={!selectedSubject || chapters.length === 0}>
              <option value="">Select chapter</option>
              {chapters.map(ch => <option key={ch._id} value={ch._id}>{ch.chapterNumber ? `Ch.${ch.chapterNumber} – ` : ''}{ch.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-14">
          <div className="w-8 h-8 spinner" />
        </div>
      )}

      {!loading && !selectedChapter && (
        <div className="card p-14 text-center">
          <RiBookOpenLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Select a class, subject, and chapter to view materials</p>
        </div>
      )}

      {!loading && selectedChapter && contents.length === 0 && mcqs.length === 0 && (
        <div className="card p-14 text-center">
          <RiFileLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No materials uploaded for this chapter yet</p>
        </div>
      )}

      {!loading && selectedChapter && (contents.length > 0 || mcqs.length > 0) && (
        <div className="space-y-5">
          {/* Chapter header */}
          <div className="card p-5">
            <p className="text-xs text-slate-400 mb-0.5">{classObj?.name} › {subjectObj?.name}</p>
            <h2 className="text-lg font-bold text-slate-800">{chapterObj?.name}</h2>
            <p className="text-xs text-slate-500 mt-1">{contents.length} material{contents.length !== 1 ? 's' : ''} · {mcqs.length} test{mcqs.length !== 1 ? 's' : ''}</p>
          </div>

          {/* Content section */}
          {contents.length > 0 && (
            <div className="card overflow-hidden">
              {/* Type tabs */}
              <div className="border-b border-slate-200 overflow-x-auto">
                <div className="flex min-w-max">
                  {CONTENT_TYPES.filter(t => t.id !== 'mcq').map(t => {
                    const count = contents.filter(c => c.type === t.id).length;
                    if (count === 0) return null;
                    const Icon = t.icon;
                    return (
                      <button key={t.id}
                        onClick={() => { setActiveType(t.id); setSelectedItem(null); }}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                          activeType === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                        }`}>
                        <Icon className="w-4 h-4" />
                        {t.label}
                        <span className="badge badge-blue py-0 px-1.5">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="p-5">
                {/* Items grid */}
                {activeType && typeItems.length > 0 && !selectedItem && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 stagger">
                    {typeItems.map(item => {
                      const t = CONTENT_TYPES.find(t => t.id === item.type);
                      const Icon = t?.icon || RiFileLine;
                      return (
                        <button key={item._id} onClick={() => setSelectedItem(item)}
                          className="card-hover p-4 text-left group animate-fade-in-up">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Icon className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors text-sm line-clamp-2">{item.title}</p>
                              {item.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.description}</p>}
                            </div>
                            <RiArrowLeftLine className="w-4 h-4 text-slate-300 group-hover:text-blue-500 rotate-180 transition-colors flex-shrink-0 mt-0.5" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Detail view */}
                {selectedItem && (
                  <ContentDetail
                    item={selectedItem}
                    onBack={() => setSelectedItem(null)}
                    fileUrl={fileUrl}
                    isYouTube={isYouTube}
                    youtubeEmbed={youtubeEmbed}
                    setFullscreenFile={setFullscreenFile}
                    setPowerPointFile={setPowerPointFile}
                  />
                )}
              </div>
            </div>
          )}

          {/* MCQ section */}
          {mcqs.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
                <RiQuestionLine className="w-4 h-4 text-violet-500" />
                <h3 className="font-semibold text-slate-800 text-sm">MCQ Tests</h3>
                <span className="badge badge-purple ml-1">{mcqs.length}</span>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
                {mcqs.map(mcq => (
                  <div key={mcq._id} className="card-hover p-4 animate-fade-in-up">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-slate-800 text-sm line-clamp-2 flex-1 mr-2">{mcq.title}</h3>
                      <span className={`badge flex-shrink-0 ${mcq.isPublished ? 'badge-green' : 'badge-yellow'}`}>
                        {mcq.isPublished ? 'Live' : 'Draft'}
                      </span>
                    </div>
                    {mcq.description && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{mcq.description}</p>}
                    <div className="grid grid-cols-3 gap-2 mb-3 text-xs text-slate-500">
                      <div className="flex flex-col items-center gap-0.5 bg-slate-50 rounded-lg p-2">
                        <RiQuestionLine className="w-3 h-3 text-slate-400" />
                        <span className="font-bold text-slate-700">{mcq.totalQuestions}</span>
                        <span>Questions</span>
                      </div>
                      <div className="flex flex-col items-center gap-0.5 bg-slate-50 rounded-lg p-2">
                        <RiTimeLine className="w-3 h-3 text-slate-400" />
                        <span className="font-bold text-slate-700">{mcq.duration}</span>
                        <span>Minutes</span>
                      </div>
                      <div className="flex flex-col items-center gap-0.5 bg-slate-50 rounded-lg p-2">
                        <RiPercentLine className="w-3 h-3 text-slate-400" />
                        <span className="font-bold text-slate-700">{mcq.passingPercentage}</span>
                        <span>Pass %</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {mcq.isPublished && (
                        <button onClick={() => handleCopyLink(mcq.shareCode)} className="btn btn-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 focus:ring-emerald-400 flex-1 text-xs">
                          <RiLinksLine className="w-3 h-3" /> Share
                        </button>
                      )}
                      <button onClick={() => { setSelectedMCQForView(mcq); setShowMCQQuestionsModal(true); }}
                        className="btn-primary btn-sm flex-1 text-xs">
                        <RiEyeLine className="w-3 h-3" /> View ({mcq.totalQuestions})
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* MCQ Questions Modal */}
      {showMCQQuestionsModal && selectedMCQForView && (
        <div className="modal-overlay animate-fade-in">
          <div className="modal-box max-w-3xl max-h-[90vh] flex flex-col animate-scale-in">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-slate-800">{selectedMCQForView.title}</h2>
                {selectedMCQForView.description && <p className="text-sm text-slate-500 mt-0.5">{selectedMCQForView.description}</p>}
              </div>
              <button onClick={() => { setShowMCQQuestionsModal(false); setSelectedMCQForView(null); }}
                className="btn-icon btn-ghost text-slate-400 flex-shrink-0 ml-4">
                <RiArrowLeftLine className="w-4 h-4 rotate-180" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                  { label: 'Questions', value: selectedMCQForView.totalQuestions, color: 'text-blue-600' },
                  { label: 'Duration (min)', value: selectedMCQForView.duration, color: 'text-emerald-600' },
                  { label: 'Pass %', value: `${selectedMCQForView.passingPercentage}%`, color: 'text-amber-600' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-slate-50 rounded-xl p-4 text-center border border-slate-100">
                    <p className={`text-2xl font-bold ${color}`}>{value}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
              {selectedMCQForView.questions?.length > 0 ? (
                <div className="space-y-4">
                  {selectedMCQForView.questions.map((q, i) => (
                    <div key={i} className="card p-4 border-l-4 border-blue-500">
                      <div className="flex items-start gap-2.5 mb-3">
                        <span className="w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                        <p className="font-semibold text-slate-800 text-sm">{q.questionText}</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-8">
                        {q.options.map(opt => (
                          <div key={opt.optionLetter} className={`flex items-center gap-2 p-2.5 rounded-lg text-sm ${opt.optionLetter === q.correctAnswer ? 'bg-emerald-50 border border-emerald-300 text-emerald-800 font-semibold' : 'bg-slate-50 border border-slate-200 text-slate-600'}`}>
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${opt.optionLetter === q.correctAnswer ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-slate-600'}`}>{opt.optionLetter}</span>
                            {opt.optionText}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card p-8 text-center text-slate-400">No questions added yet</div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end flex-shrink-0">
              <button onClick={() => { setShowMCQQuestionsModal(false); setSelectedMCQForView(null); }} className="btn-primary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen PDF */}
      {fullscreenFile && (
        <FullscreenPDF file={fullscreenFile} fileUrl={fileUrl} onClose={() => setFullscreenFile(null)} />
      )}
    </div>
  );
};

/* ── ContentDetail ────────────────────────────────────────────── */
const ContentDetail = ({ item, onBack, fileUrl, isYouTube, youtubeEmbed, setFullscreenFile, setPowerPointFile }) => {
  const t      = CONTENT_TYPES.find(t => t.id === item.type);
  const Icon   = t?.icon || RiFileLine;
  const url    = item.link || fileUrl(item.filePath, item._id);
  const embedUrl = isYouTube(item.link) ? youtubeEmbed(item.link) : null;

  return (
    <div className="animate-fade-in-up">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium mb-4 transition-colors">
        <RiArrowLeftLine className="w-4 h-4" /> Back to list
      </button>

      <div className="card overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-slate-800 truncate">{item.title}</h2>
            <span className="badge badge-blue text-xs">{t?.label}</span>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {item.description && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
              <p className="text-sm text-slate-700 leading-relaxed">{item.description}</p>
            </div>
          )}

          {/* YouTube embed */}
          {embedUrl && (
            <div className="rounded-xl overflow-hidden aspect-video bg-black shadow-sm">
              <iframe src={embedUrl} title={item.title} className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          )}

          {/* Video link (non-YouTube) */}
          {item.type === 'video' && item.link && !embedUrl && (
            <a href={item.link} target="_blank" rel="noreferrer" className="btn-primary w-full">
              <RiVideoLine className="w-4 h-4" /> Watch Video
            </a>
          )}

          {/* Reference / MCQ link */}
          {(item.type === 'reference' || item.type === 'mcq') && item.link && (
            <a href={item.link} target="_blank" rel="noreferrer" className="btn-primary w-full">
              <RiExternalLinkLine className="w-4 h-4" /> {item.type === 'mcq' ? 'Open MCQ Test' : 'Open Reference'}
            </a>
          )}

          {/* Linked MCQ */}
          {item.mcqId && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-3">
              <p className="text-sm font-medium text-violet-800 flex items-center gap-2">
                <RiQuestionLine className="w-4 h-4" /> Linked MCQ: {item.mcqId.title || 'MCQ Test'}
              </p>
            </div>
          )}

          {/* PDF inline preview */}
          {item.filePath && item.fileType === 'application/pdf' && (
            <>
              <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5">
                <span className="text-sm font-medium text-blue-700 flex items-center gap-2">
                  <RiEyeLine className="w-4 h-4" /> View-Only Mode
                </span>
                <button onClick={() => setFullscreenFile(item)}
                  className="btn-primary btn-sm">
                  <RiFullscreenLine className="w-3.5 h-3.5" /> Fullscreen
                </button>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm" style={{ height: 560 }}>
                <PdfViewer src={fileUrl(item.filePath, item._id)} style={{ width: '100%', height: '100%' }} />
              </div>
            </>
          )}

          {/* PowerPoint */}
          {item.filePath && (item.fileType === 'application/vnd.ms-powerpoint' || item.fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') && (
            <PowerPointViewer contentId={item._id} directFileUrl={fileUrl(item.filePath, item._id)} fileName={item.title} onClose={() => setPowerPointFile(null)} />
          )}

          {/* Other files */}
          {item.filePath && item.fileType !== 'application/pdf' && item.fileType !== 'application/vnd.ms-powerpoint' && item.fileType !== 'application/vnd.openxmlformats-officedocument.presentationml.presentation' && (
            <a href={fileUrl(item.filePath, item._id)} target="_blank" rel="noreferrer" className="btn-primary w-full">
              <RiEyeLine className="w-4 h-4" /> View {t?.label}
              {item.fileName && <span className="opacity-80 text-xs ml-1">({item.fileName})</span>}
            </a>
          )}

          {item.questionCount && (
            <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm font-medium text-amber-800">
              <RiFileTextLine className="w-4 h-4" /> {item.questionCount} Questions
            </div>
          )}

          {!url && !item.mcqId && (
            <div className="card p-8 text-center text-slate-400 text-sm">No file or link attached</div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── FullscreenPDF (unchanged logic) ─────────────────────────── */
const FullscreenPDF = ({ file, fileUrl, onClose }) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
    else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
  }, []);

  useEffect(() => {
    const handleFscChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) onClose();
    };
    document.addEventListener('fullscreenchange', handleFscChange);
    document.addEventListener('webkitfullscreenchange', handleFscChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFscChange);
      document.removeEventListener('webkitfullscreenchange', handleFscChange);
    };
  }, [onClose]);

  const handleClose = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else onClose();
  };

  return (
    <div ref={wrapRef} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#000', width: '100%', height: '100%' }}>
      <button onClick={handleClose} title="Close (Esc)" style={{ position: 'absolute', top: 10, right: 10, zIndex: 10, width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
      <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 10, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12, padding: '5px 12px', borderRadius: 20, maxWidth: '40vw', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.title}</div>
      <PdfViewer src={fileUrl(file.filePath, file._id)} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default TeacherMaterials;
